import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  findAllPurchaseOrder,
  updatePurchaseOrderStatus,
} from "../../../../api/inventoryService";
import {
  ORDER_PAGE_SIZE,
  ORDER_STATUS_LABEL,
  OrderStatus,
  filterOrders,
  receivedPercent,
} from "../../purchaseOrderUtils";
import { pageSlice } from "../../purchaseListUtils";
import BottomTab from "../../../../components/pagination/BottomTab";
import { TableSkeleton } from "../../../../components/skeleton/ListSkeleton";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AddIcon from "@mui/icons-material/Add";

const STATUS_STYLE: Record<OrderStatus, string> = {
  open: "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300",
  closed:
    "bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300",
  cancelled: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
};

const FILTERS: Array<{ label: string; value: string }> = [
  { label: "All", value: "" },
  { label: "Open", value: "open" },
  { label: "Closed", value: "closed" },
  { label: "Cancelled", value: "cancelled" },
];

function AllPurchaseOrder() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    findAllPurchaseOrder(status)
      .then((data) => setOrders(data as any[]))
      .catch((error) => console.error(error?.response))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = useCallback(
    (id: string, next: OrderStatus) => {
      updatePurchaseOrderStatus(id, next)
        .then(load)
        .catch((error) => console.error(error?.response));
    },
    [load]
  );

  const filtered = useMemo(
    () => filterOrders(orders, { search, from, to }),
    [orders, search, from, to]
  );

  // BottomTab holds its own page number, so it and the slice reset together
  // whenever the filters change what is being paged through.
  const filterKey = `${status}|${search}|${from}|${to}`;
  useEffect(() => setPage(1), [filterKey]);

  // The reset effect lands a render late, so the page is clamped here too -
  // a filter that shrinks the list must not flash an empty table first.
  const lastPage = Math.max(1, Math.ceil(filtered.length / ORDER_PAGE_SIZE));
  const pageRows = pageSlice(filtered, Math.min(page, lastPage), ORDER_PAGE_SIZE);
  const hasFilters = Boolean(search || from || to) || status !== "";
  const showSkeleton = loading && orders.length === 0;

  const clearFilters = () => {
    setStatus("");
    setSearch("");
    setFrom("");
    setTo("");
  };

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-violet-100 dark:bg-violet-900/40 rounded-md flex items-center justify-center">
              <ReceiptLongIcon className="text-violet-600 dark:text-violet-300" sx={{ fontSize: 14 }} />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Purchase Orders
              </h1>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">
                One order can be billed across several supplier invoices.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 dark:bg-violet-900/40 rounded-lg">
              <ReceiptLongIcon className="text-violet-500 dark:text-violet-300" sx={{ fontSize: 16 }} />
              <span className="font-bold text-xs text-violet-600 dark:text-violet-300">
                {loading ? "…" : filtered.length}
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-[10px]">
                {hasFilters ? `of ${orders.length} Orders` : "Orders"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/client/inventory/purchase-order/add-order")}
              className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
            >
              <AddIcon sx={{ fontSize: 14 }} />
              New Order
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setStatus(filter.value)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                status === filter.value
                  ? "bg-primary text-white"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[11px] text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[11px] text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order or supplier"
              className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
            />
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-[11px] font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  {["Order", "Supplier", "Date", "Received", "Status", ""].map(
                    (title, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                      >
                        {title}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {showSkeleton ? (
                  <TableSkeleton rows={6} colSpan={6} />
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                      <ReceiptLongIcon sx={{ fontSize: 32 }} className="mb-2" />
                      <p className="text-xs">No purchase orders</p>
                    </td>
                  </tr>
                ) : (
                  pageRows.map((order: any) => {
                    const percent = receivedPercent(
                      order?.totalReceived,
                      order?.totalOrdered
                    );
                    return (
                      <tr key={order._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="px-3 py-2">
                          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                            {order?.orderNo}
                          </span>
                          {order?.linkedInvoiceCount > 0 && (
                            <span className="block text-[10px] text-slate-400 dark:text-slate-500">
                              {order.linkedInvoiceCount} invoice
                              {order.linkedInvoiceCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {order?.supplierName}
                          {order?.supplierCompany && (
                            <span className="block text-[10px] text-slate-400 dark:text-slate-500">
                              {order.supplierCompany}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {order?.orderDate}
                          {order?.expectedDate && (
                            <span className="block text-[10px] text-slate-400 dark:text-slate-500">
                              due {order.expectedDate}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-600 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  order?.fullyReceived
                                    ? "bg-emerald-500"
                                    : "bg-amber-400"
                                }`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 shrink-0">
                              {percent}%
                            </span>
                          </div>
                          {/* Status is set by hand, so flag the ones that look
                              ready to close rather than closing them. */}
                          {order?.fullyReceived && order?.status === "open" && (
                            <span className="block text-[10px] text-emerald-600 dark:text-emerald-300 mt-0.5">
                              fully received — ready to close
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                              STATUS_STYLE[order?.status as OrderStatus] ??
                              STATUS_STYLE.closed
                            }`}
                          >
                            {ORDER_STATUS_LABEL[order?.status as OrderStatus] ??
                              order?.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/client/inventory/purchase-order/edit-order/${order._id}`
                                )
                              }
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all text-xs font-medium"
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                              Open
                            </button>
                            {order?.status === "open" ? (
                              <button
                                type="button"
                                onClick={() => changeStatus(order._id, "closed")}
                                className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-xs font-medium"
                              >
                                Close
                              </button>
                            ) : order?.status === "closed" ? (
                              <button
                                type="button"
                                onClick={() => changeStatus(order._id, "open")}
                                className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-xs font-medium"
                              >
                                Reopen
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-700">
          <BottomTab
            key={filterKey}
            title="Orders"
            count={filtered.length}
            onPageChange={setPage}
            pageSize={ORDER_PAGE_SIZE}
          />
        </div>
      </div>
    </div>
  );
}

export default AllPurchaseOrder;
